<template>
  <AdminLayout>
    <div class="px-6 py-6">
      <!-- 페이지 헤더 -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center">
            <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-[22px] font-bold text-slate-900">파트너 공지사항</h1>
            <p class="text-[14px] text-slate-400">파트너스센터에 표시되는 공지사항을 관리합니다</p>
          </div>
        </div>
      </div>
      <!-- 새 공지사항 작성 -->
      <div class="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <h3 class="text-[16px] font-bold text-slate-900 mb-4">새 공지사항 작성</h3>

        <form @submit.prevent="createNotice" class="space-y-4">
          <div>
            <label class="block text-[13px] font-bold text-slate-700 mb-2">제목</label>
            <input
              v-model="newNotice.title"
              type="text"
              placeholder="공지사항 제목을 입력하세요"
              class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
              required
            />
          </div>

          <div>
            <label class="block text-[13px] font-bold text-slate-700 mb-2">내용</label>
            <!-- 에디터 툴바 -->
            <div class="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border border-b-0 border-slate-200 rounded-t-xl">
              <button
                type="button"
                @click="formatText('bold')"
                class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                title="굵게"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z"/>
                </svg>
              </button>
              <button
                type="button"
                @click="formatText('italic')"
                class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                title="기울임"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 4h4m-2 0l-4 16m0 0h4"/>
                </svg>
              </button>
              <button
                type="button"
                @click="formatText('underline')"
                class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                title="밑줄"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v7a5 5 0 0010 0V4M5 20h14"/>
                </svg>
              </button>
              <div class="w-px h-5 bg-slate-300 mx-1"></div>
              <button
                type="button"
                @click="formatText('insertUnorderedList')"
                class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                title="목록"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </button>
              <div class="w-px h-5 bg-slate-300 mx-1"></div>
              <label
                class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition cursor-pointer"
                title="이미지 삽입"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleImageUpload($event, 'new')"
                />
              </label>
              <span v-if="uploading" class="text-xs text-slate-500 ml-2">업로드 중...</span>
            </div>
            <!-- 에디터 본문 -->
            <div
              ref="newEditorRef"
              contenteditable="true"
              @input="handleNewEditorInput"
              class="w-full min-h-[200px] px-4 py-3 border border-slate-200 rounded-b-xl text-[14px] text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition overflow-y-auto"
              style="max-height: 400px;"
            ></div>
          </div>

          <div class="flex items-center gap-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="newNotice.isPinned"
                type="checkbox"
                class="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span class="text-[14px] font-medium text-slate-700">상단 고정</span>
            </label>

            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="newNotice.isActive"
                type="checkbox"
                class="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span class="text-[14px] font-medium text-slate-700">즉시 공개</span>
            </label>
          </div>

          <div class="flex justify-end">
            <button
              type="submit"
              :disabled="creating"
              class="px-6 py-3 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {{ creating ? '등록 중...' : '공지사항 등록' }}
            </button>
          </div>
        </form>
      </div>

      <!-- 공지사항 목록 -->
      <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-100">
          <h3 class="text-[16px] font-bold text-slate-900">공지사항 목록</h3>
        </div>

        <div v-if="loading" class="p-8 text-center">
          <div class="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>

        <div v-else-if="notices.length === 0" class="p-8 text-center">
          <p class="text-[14px] text-slate-400">등록된 공지사항이 없습니다</p>
        </div>

        <div v-else class="divide-y divide-slate-100">
          <div
            v-for="notice in notices"
            :key="notice.id"
            class="p-4 hover:bg-slate-50 transition-colors"
          >
            <div class="flex items-start gap-4">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span v-if="notice.isPinned" class="px-2 py-0.5 bg-amber-100 text-amber-700 text-[11px] font-bold rounded">고정</span>
                  <span
                    class="px-2 py-0.5 text-[11px] font-bold rounded"
                    :class="notice.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'"
                  >
                    {{ notice.isActive ? '공개' : '비공개' }}
                  </span>
                  <h4 class="text-[15px] font-bold text-slate-900 truncate">{{ notice.title }}</h4>
                </div>
                <div class="text-[13px] text-slate-500 line-clamp-2 mb-2" v-html="getPlainText(notice.content)"></div>
                <p class="text-[12px] text-slate-400">{{ formatDate(notice.createdAt) }}</p>
              </div>

              <div class="flex items-center gap-2 flex-shrink-0">
                <button
                  @click="toggleActive(notice)"
                  class="px-3 py-1.5 text-[12px] font-medium rounded-lg transition-colors"
                  :class="notice.isActive ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'"
                >
                  {{ notice.isActive ? '비공개' : '공개' }}
                </button>
                <button
                  @click="togglePinned(notice)"
                  class="px-3 py-1.5 text-[12px] font-medium rounded-lg transition-colors"
                  :class="notice.isPinned ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
                >
                  {{ notice.isPinned ? '고정 해제' : '고정' }}
                </button>
                <button
                  @click="previewNotice(notice)"
                  class="px-3 py-1.5 bg-slate-100 text-slate-600 text-[12px] font-medium rounded-lg hover:bg-slate-200 transition-colors"
                >
                  미리보기
                </button>
                <button
                  @click="editNotice(notice)"
                  class="px-3 py-1.5 bg-blue-100 text-blue-700 text-[12px] font-medium rounded-lg hover:bg-blue-200 transition-colors"
                >
                  수정
                </button>
                <button
                  @click="deleteNotice(notice)"
                  class="px-3 py-1.5 bg-red-100 text-red-700 text-[12px] font-medium rounded-lg hover:bg-red-200 transition-colors"
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 미리보기 모달 -->
      <div v-if="previewingNotice" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 class="text-[16px] font-bold text-slate-900">{{ previewingNotice.title }}</h3>
            <button @click="previewingNotice = null" class="text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6 overflow-y-auto notice-content" v-html="previewingNotice.content"></div>
        </div>
      </div>

      <!-- 수정 모달 -->
      <div v-if="editingNotice" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <div class="px-6 py-4 border-b border-slate-100">
            <h3 class="text-[16px] font-bold text-slate-900">공지사항 수정</h3>
          </div>

          <form @submit.prevent="updateNotice" class="p-6 space-y-4 overflow-y-auto">
            <div>
              <label class="block text-[13px] font-bold text-slate-700 mb-2">제목</label>
              <input
                v-model="editingNotice.title"
                type="text"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                required
              />
            </div>

            <div>
              <label class="block text-[13px] font-bold text-slate-700 mb-2">내용</label>
              <!-- 에디터 툴바 -->
              <div class="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border border-b-0 border-slate-200 rounded-t-xl">
                <button
                  type="button"
                  @click="formatText('bold', 'edit')"
                  class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                  title="굵게"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z"/>
                  </svg>
                </button>
                <button
                  type="button"
                  @click="formatText('italic', 'edit')"
                  class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                  title="기울임"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 4h4m-2 0l-4 16m0 0h4"/>
                  </svg>
                </button>
                <button
                  type="button"
                  @click="formatText('underline', 'edit')"
                  class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                  title="밑줄"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v7a5 5 0 0010 0V4M5 20h14"/>
                  </svg>
                </button>
                <div class="w-px h-5 bg-slate-300 mx-1"></div>
                <button
                  type="button"
                  @click="formatText('insertUnorderedList', 'edit')"
                  class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition"
                  title="목록"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
                  </svg>
                </button>
                <div class="w-px h-5 bg-slate-300 mx-1"></div>
                <label
                  class="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 transition cursor-pointer"
                  title="이미지 삽입"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <input
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleImageUpload($event, 'edit')"
                  />
                </label>
                <span v-if="uploading" class="text-xs text-slate-500 ml-2">업로드 중...</span>
              </div>
              <!-- 에디터 본문 -->
              <div
                ref="editEditorRef"
                contenteditable="true"
                @input="handleEditEditorInput"
                class="w-full min-h-[200px] px-4 py-3 border border-slate-200 rounded-b-xl text-[14px] text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition overflow-y-auto"
                style="max-height: 400px;"
              ></div>
            </div>

            <div class="flex gap-3 justify-end pt-2">
              <button
                type="button"
                @click="editingNotice = null"
                class="px-4 py-2.5 bg-slate-100 text-slate-700 text-[14px] font-medium rounded-xl hover:bg-slate-200 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                :disabled="updating"
                class="px-4 py-2.5 bg-blue-600 text-white text-[14px] font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {{ updating ? '저장 중...' : '저장' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, onMounted, nextTick, watch } from 'vue'
import { collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'PartnerNoticeManageView',
  components: {
    AdminLayout
  },
  setup() {
    const loading = ref(true)
    const creating = ref(false)
    const updating = ref(false)
    const uploading = ref(false)
    const notices = ref([])
    const editingNotice = ref(null)
    const previewingNotice = ref(null)

    const newEditorRef = ref(null)
    const editEditorRef = ref(null)

    const newNotice = ref({
      title: '',
      content: '',
      isPinned: false,
      isActive: true
    })

    // 수정 모달이 열릴 때 에디터 내용 설정
    watch(editingNotice, async (notice) => {
      if (notice) {
        await nextTick()
        if (editEditorRef.value) {
          editEditorRef.value.innerHTML = notice.content || ''
        }
      }
    })

    const handleNewEditorInput = () => {
      if (newEditorRef.value) {
        newNotice.value.content = newEditorRef.value.innerHTML
      }
    }

    const handleEditEditorInput = () => {
      if (editEditorRef.value && editingNotice.value) {
        editingNotice.value.content = editEditorRef.value.innerHTML
      }
    }

    const formatText = (command, target = 'new') => {
      document.execCommand(command, false, null)
      if (target === 'new' && newEditorRef.value) {
        newEditorRef.value.focus()
      } else if (target === 'edit' && editEditorRef.value) {
        editEditorRef.value.focus()
      }
    }

    const compressImage = (file, maxWidth = 800, quality = 0.8) => {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            let width = img.width
            let height = img.height

            if (width > maxWidth) {
              height = (height * maxWidth) / width
              width = maxWidth
            }

            canvas.width = width
            canvas.height = height

            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0, width, height)

            canvas.toBlob(
              (blob) => resolve(blob),
              'image/webp',
              quality
            )
          }
          img.src = e.target.result
        }
        reader.readAsDataURL(file)
      })
    }

    const handleImageUpload = async (event, target) => {
      const file = event.target.files?.[0]
      if (!file) return

      uploading.value = true
      try {
        // 이미지 압축
        const compressedBlob = await compressImage(file)
        const timestamp = Date.now()
        const fileName = `partner-notices/${timestamp}.webp`
        const imageRef = storageRef(storage, fileName)

        await uploadBytes(imageRef, compressedBlob)
        const url = await getDownloadURL(imageRef)

        // 에디터에 이미지 삽입
        const editorRef = target === 'new' ? newEditorRef : editEditorRef
        if (editorRef.value) {
          editorRef.value.focus()
          document.execCommand('insertImage', false, url)
        }
      } catch (error) {
        console.error('이미지 업로드 실패:', error)
        alert('이미지 업로드에 실패했습니다.')
      } finally {
        uploading.value = false
        event.target.value = ''
      }
    }

    const getPlainText = (html) => {
      if (!html) return ''
      const temp = document.createElement('div')
      temp.innerHTML = html
      return temp.textContent || temp.innerText || ''
    }

    const fetchNotices = async () => {
      loading.value = true
      try {
        const q = query(
          collection(db, 'partnerNotices'),
          orderBy('isPinned', 'desc'),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        notices.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('공지사항 조회 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const createNotice = async () => {
      if (!newNotice.value.title || !newNotice.value.content) return

      creating.value = true
      try {
        await addDoc(collection(db, 'partnerNotices'), {
          title: newNotice.value.title,
          content: newNotice.value.content,
          isPinned: newNotice.value.isPinned,
          isActive: newNotice.value.isActive,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })

        newNotice.value = {
          title: '',
          content: '',
          isPinned: false,
          isActive: true
        }
        if (newEditorRef.value) {
          newEditorRef.value.innerHTML = ''
        }

        await fetchNotices()
        alert('공지사항이 등록되었습니다.')
      } catch (error) {
        console.error('공지사항 등록 실패:', error)
        alert('공지사항 등록에 실패했습니다.')
      } finally {
        creating.value = false
      }
    }

    const toggleActive = async (notice) => {
      try {
        await updateDoc(doc(db, 'partnerNotices', notice.id), {
          isActive: !notice.isActive,
          updatedAt: new Date().toISOString()
        })
        await fetchNotices()
      } catch (error) {
        console.error('상태 변경 실패:', error)
        alert('상태 변경에 실패했습니다.')
      }
    }

    const togglePinned = async (notice) => {
      try {
        await updateDoc(doc(db, 'partnerNotices', notice.id), {
          isPinned: !notice.isPinned,
          updatedAt: new Date().toISOString()
        })
        await fetchNotices()
      } catch (error) {
        console.error('고정 상태 변경 실패:', error)
        alert('고정 상태 변경에 실패했습니다.')
      }
    }

    const previewNotice = (notice) => {
      previewingNotice.value = { ...notice }
    }

    const editNotice = (notice) => {
      editingNotice.value = { ...notice }
    }

    const updateNotice = async () => {
      if (!editingNotice.value) return

      updating.value = true
      try {
        await updateDoc(doc(db, 'partnerNotices', editingNotice.value.id), {
          title: editingNotice.value.title,
          content: editingNotice.value.content,
          updatedAt: new Date().toISOString()
        })

        editingNotice.value = null
        await fetchNotices()
        alert('공지사항이 수정되었습니다.')
      } catch (error) {
        console.error('공지사항 수정 실패:', error)
        alert('공지사항 수정에 실패했습니다.')
      } finally {
        updating.value = false
      }
    }

    const deleteNotice = async (notice) => {
      if (!confirm('이 공지사항을 삭제하시겠습니까?')) return

      try {
        await deleteDoc(doc(db, 'partnerNotices', notice.id))
        await fetchNotices()
        alert('공지사항이 삭제되었습니다.')
      } catch (error) {
        console.error('공지사항 삭제 실패:', error)
        alert('공지사항 삭제에 실패했습니다.')
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    onMounted(() => {
      fetchNotices()
    })

    return {
      loading,
      creating,
      updating,
      uploading,
      notices,
      newNotice,
      editingNotice,
      previewingNotice,
      newEditorRef,
      editEditorRef,
      handleNewEditorInput,
      handleEditEditorInput,
      formatText,
      handleImageUpload,
      getPlainText,
      createNotice,
      toggleActive,
      togglePinned,
      previewNotice,
      editNotice,
      updateNotice,
      deleteNotice,
      formatDate
    }
  }
}
</script>

<style scoped>
/* 에디터 내부 이미지 스타일 */
[contenteditable] :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 8px 0;
}

/* 미리보기 내용 스타일 */
.notice-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 12px 0;
}

.notice-content :deep(ul) {
  list-style: disc;
  padding-left: 20px;
  margin: 8px 0;
}

.notice-content :deep(ol) {
  list-style: decimal;
  padding-left: 20px;
  margin: 8px 0;
}

.notice-content :deep(p) {
  margin: 8px 0;
}

.notice-content :deep(b),
.notice-content :deep(strong) {
  font-weight: 600;
}
</style>
